import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { Stack, Alert } from "@mui/material"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  useCardPriceQuery,
  useUserCardsQuery,
  useValidateWtpImperativeQuery,
} from "@/queries/hooks"
import { PassHolderFormState } from "@/types/PassHolder"
import { isUserProfileCard } from "@/utils/wtpUtils"

import {
  MemberCouponField,
  ValidatedCoupon,
  InsuranceField,
  LastNameField,
  ImageField,
  PersonCertificateField,
  WtpNumberField,
  BirthDateField,
  FirstNameField,
  NewCardInfo,
  PassSelectionField,
} from "./components"
import { useInsurance, useScrollToFieldError } from "./hooks"
import { createPassHolderSchema, PassHolderFormData } from "./schema"
import { createWtpNumberValidationRefinement } from "./schema/refinements/WtpNumberRefinements"
import { shouldShowAlert } from "./utils"

type PassHolderFormProps = {
  passHolder: PassHolderFormState
  onSave: (updatedPassHolder: PassHolderFormState) => void
  usedWtpNumbers: string[]
  formId: string
  isEditMode?: boolean
  startDate: Date
  resortExternalId: number
}

export type PassHolderFormRef = {
  validateAndScrollToError: () => Promise<boolean>
}

const PassHolderForm = forwardRef<PassHolderFormRef, PassHolderFormProps>(
  (
    {
      passHolder,
      onSave,
      usedWtpNumbers,
      formId,
      isEditMode = false,
      startDate,
      resortExternalId,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { validateWtpAsync } = useValidateWtpImperativeQuery(resortExternalId)
    const { data: userCards = [] } = useUserCardsQuery()

    const validateWtpWithProfileCard = useCallback(
      (wtpNumber: string) => {
        return validateWtpAsync(
          wtpNumber,
          isUserProfileCard(userCards, wtpNumber)
        )
      },
      [validateWtpAsync, userCards]
    )

    const [requireInsurance, setRequireInsurance] = useState(true)

    const validationSchema = useMemo(
      () =>
        createPassHolderSchema(
          passHolder.idImageRequired,
          passHolder.personCertificateRequired,
          passHolder.minAge,
          passHolder.maxAge,
          startDate,
          [createWtpNumberValidationRefinement(validateWtpWithProfileCard)],
          requireInsurance
        ),
      [
        passHolder.idImageRequired,
        passHolder.personCertificateRequired,
        passHolder.minAge,
        passHolder.maxAge,
        startDate,
        validateWtpWithProfileCard,
        requireInsurance,
      ]
    )

    const formMethods = useForm<PassHolderFormData>({
      resolver: zodResolver(validationSchema),
      mode: "onSubmit",
      reValidateMode: "onChange",
      defaultValues: {
        firstName: passHolder.passHolder.firstName || "",
        lastName: passHolder.passHolder.lastName || "",
        birthDate: passHolder.passHolder.birthDate || "",
        wtpNumber: passHolder.wtpNumber || "",
        insurance: passHolder.insurance,
        passSelection: passHolder.passSelection || "existing",
        passHolderImage: passHolder.passHolderImage || {
          id: "",
          base64: "",
          fileName: "",
        },
        personCertificate: passHolder.personCertificate || {
          id: "",
          fileName: "",
        },
        exitingPhotoId: passHolder.exitingPhotoId ?? null,
      },
    })

    const { handleSubmit, control, formState } = formMethods
    const { scrollToFirstError } = useScrollToFieldError(formId)

    useImperativeHandle(ref, () => ({
      validateAndScrollToError: () =>
        new Promise<boolean>((resolve) => {
          handleSubmit(
            () => resolve(true),
            (errors) => {
              scrollToFirstError(errors)
              resolve(false)
            }
          )()
        }),
    }))

    const [
      firstName = "",
      lastName = "",
      birthDate = "",
      wtpNumber = "",
      passSelection,
    ] = useWatch({
      control,
      name: [
        "firstName",
        "lastName",
        "birthDate",
        "wtpNumber",
        "passSelection",
      ],
    })

    const hasFormErrors = !!(
      formState.errors.firstName ||
      formState.errors.lastName ||
      formState.errors.birthDate
    )

    const {
      insurance,
      isLoading: isInsuranceLoading,
      error: insuranceError,
      isUserStillTyping,
    } = useInsurance({
      firstName,
      lastName,
      birthDate,
      bookingLengthInDays: passHolder.bookingLengthInDays,
      resortId: passHolder.resortId,
      startDate,
      hasFormErrors,
      isSubscriptionProduct: passHolder.isSubscriptionProduct ?? true,
    })

    useEffect(() => {
      setRequireInsurance(!insuranceError)
      if (insuranceError) {
        formMethods.clearErrors("insurance")
        formMethods.setValue("insurance", undefined, { shouldValidate: false })
      }
    }, [insuranceError, formMethods])

    useEffect(() => {
      if (isUserStillTyping) {
        formMethods.setValue("insurance", undefined, { shouldValidate: false })
      }
    }, [isUserStillTyping, formMethods])

    const { data: cardPrice, isLoading: isLoadingCardPrice } =
      useCardPriceQuery({
        productId: passHolder.productPriceId,
        arrivalDate: startDate,
        resortId: resortExternalId,
      })
    const selectedCard =
      userCards.find((c) => c.wtpNumber === wtpNumber) || null

    const showAlert =
      passSelection === "existing" &&
      shouldShowAlert(selectedCard, firstName, lastName, birthDate)

    const [validatedCoupon, setValidatedCoupon] =
      useState<ValidatedCoupon | null>(null)

    const onSubmit = (data: PassHolderFormData) => {
      const updatedPassHolder: PassHolderFormState = {
        ...passHolder,
        insurance: data.insurance,
        passHolder: {
          ...passHolder.passHolder,
          firstName: data.firstName,
          lastName: data.lastName,
          birthDate: data.birthDate,
        },
        wtpNumber: data.wtpNumber || "",
        passSelection: data.passSelection,
        passHolderImage: data.passHolderImage,
        personCertificate: data.personCertificate,
        memberCoupon: validatedCoupon || undefined,
        insurancePrice: data.insurance ? insurance?.price : undefined,
        cardPrice: data.passSelection === "new" ? cardPrice?.price : undefined,
        exitingPhotoId: data.exitingPhotoId ?? null,
        insuranceAnalyticsName: data.insurance
          ? insurance?.analyticsName
          : undefined,
        insuranceAnalyticsId: data.insurance
          ? insurance?.analyticsId
          : undefined,
      }
      onSave(updatedPassHolder)
    }

    const handleCouponSuccess = useCallback((coupon: ValidatedCoupon) => {
      setValidatedCoupon(coupon)
    }, [])

    return (
      <FormProvider {...formMethods}>
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit, scrollToFirstError)}
          id={formId}
          spacing={2}
        >
          <PassSelectionField />
          {passSelection === "new" && (
            <NewCardInfo isLoading={isLoadingCardPrice} cardPrice={cardPrice} />
          )}
          {passSelection === "existing" && (
            <WtpNumberField
              usedWtpNumbers={usedWtpNumbers}
              resortExternalId={resortExternalId}
            />
          )}
          <FirstNameField
            minAge={passHolder.minAge}
            maxAge={passHolder.maxAge}
          />
          <LastNameField />
          <BirthDateField />
          {showAlert && (
            <Alert severity="info">
              {t("passHolderDetails.form.gamesAlert")}
            </Alert>
          )}
          {passHolder.idImageRequired && <ImageField />}
          {passHolder.personCertificateRequired && <PersonCertificateField />}

          {passHolder.showFreeSkiDays && (
            <MemberCouponField
              onSuccess={(coupon) => handleCouponSuccess(coupon)}
              couponCode={validatedCoupon?.couponCode}
              onResetCoupon={() => setValidatedCoupon(null)}
            />
          )}
          <InsuranceField
            insuranceData={insurance}
            isLoading={isInsuranceLoading}
            ageGroup={passHolder.ageGroup}
            bookingLengthInDays={passHolder.bookingLengthInDays}
            isSubscriptionProduct={passHolder.isSubscriptionProduct}
            priceFetchError={insuranceError?.message}
            isEditMode={isEditMode}
          />
        </Stack>
      </FormProvider>
    )
  }
)

export default PassHolderForm
